import { MAX_MAP_DIM } from "daedalus-core";
import clock from "../time/clock.json";
import { GAME_EPOCH_MS, TURN_MS, gameDayOf } from "../time/game-clock.ts";
import { SKIES, type Rgb, type SkyOverride } from "./sky.ts";

/**
 * The sky's light at a moment: the ambient (0 dark to 1 full light), the tint colour and the alpha of the darkness fill
 * (ADR-141 decision D1 and its amendment of 2026-10-31). Written in place by `ambientAt` so a frame allocates nothing.
 */
export interface Ambient {
  ambient: number;
  r: number;
  g: number;
  b: number;
  tint: number;
}

/**
 * A fresh `Ambient`, for the caller that keeps one and rewrites it.
 *
 * @returns An object at full light.
 */
export function newAmbient(): Ambient {
  return { ambient: 1, r: 255, g: 255, b: 255, tint: 0 };
}

const MONTH_DAYS = 33;
const WEEK_DAYS = 11;
const GREATER_FULL = 16;
const LESSER_FULL = 5;
const GREATER_WEIGHT = 0.7;
const MINUTES_PER_TURN = TURN_MS / 60_000;
const PHASES = clock.turnPhaseMinutes;
const DAWN_END = PHASES.dawn;
const DUSK_START = PHASES.dawn + PHASES.day;
const DUSK_END = DUSK_START + PHASES.dusk;
const TINT_SPLIT = 0.55;

/**
 * Smoothstep of a unit progress, clamped.
 *
 * @param p The progress.
 * @returns 0 to 1.
 */
function smooth(p: number): number {
  const c = p < 0 ? 0 : p > 1 ? 1 : p;
  return c * c * (3 - 2 * c);
}

/**
 * The whole day of a calendar day, or 0 when it is below 0 or not finite.
 *
 * @param day The day.
 * @returns A whole day, 0 or more.
 */
function wholeDay(day: number): number {
  return Number.isFinite(day) && day > 0 ? Math.floor(day) : 0;
}

/**
 * The greater moon's illumination on a calendar day: new on the first day of the 33-day month, full on its 17th.
 *
 * @param day The game day, 0 for the first.
 * @returns 0 to 1.
 */
export function greaterMoon(day: number): number {
  const d = wholeDay(day) % MONTH_DAYS;
  return d <= GREATER_FULL ? d / GREATER_FULL : (MONTH_DAYS - d) / (MONTH_DAYS - GREATER_FULL);
}

/**
 * The lesser moon's illumination on a calendar day: new on the first day of the 11-day week, full on its sixth.
 *
 * @param day The game day, 0 for the first.
 * @returns 0 to 1.
 */
export function lesserMoon(day: number): number {
  const d = wholeDay(day) % WEEK_DAYS;
  return d <= LESSER_FULL ? d / LESSER_FULL : (WEEK_DAYS - d) / (WEEK_DAYS - LESSER_FULL);
}

/**
 * How bright the night is on a calendar day, from the moons alone: the greater moon weighs 0.7 and the lesser 0.3.
 *
 * @param day The game day, 0 for the first.
 * @returns 0 on the Dark of Both (day 1) to 1 on the Twin Full (day 17).
 */
export function moonBrightness(day: number): number {
  return GREATER_WEIGHT * greaterMoon(day) + (1 - GREATER_WEIGHT) * lesserMoon(day);
}

/**
 * The night's ambient on a calendar day under a sky.
 *
 * @param day The game day.
 * @param sky The sky in force.
 * @returns The ambient the dark holds, between the sky's darkest and brightest.
 */
export function nightLevel(day: number, sky: SkyOverride): number {
  const table = SKIES[sky];
  return table.darkest + (table.brightest - table.darkest) * moonBrightness(day);
}

/**
 * The minute of the turn a time falls in.
 *
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns 0 up to the turn's length in minutes; 0 for a time before the epoch or one that is not finite.
 */
export function turnMinuteOf(epochMs: number): number {
  return Number.isFinite(epochMs) && epochMs > GAME_EPOCH_MS ? ((epochMs - GAME_EPOCH_MS) % TURN_MS) / 60_000 : 0;
}

/**
 * Writes the mix of two colours into `out`.
 *
 * @param a The colour at 0.
 * @param b The colour at 1.
 * @param t The mix, 0 to 1.
 * @param out The ambient to write.
 */
function mixInto(a: Rgb, b: Rgb, t: number, out: Ambient): void {
  out.r = a[0] + (b[0] - a[0]) * t;
  out.g = a[1] + (b[1] - a[1]) * t;
  out.b = a[2] + (b[2] - a[2]) * t;
}

/**
 * Writes one colour into `out`.
 *
 * @param colour The colour.
 * @param out The ambient to write.
 */
function holdInto(colour: Rgb, out: Ambient): void {
  out.r = colour[0];
  out.g = colour[1];
  out.b = colour[2];
}

/**
 * Writes the colour of a leg of the turn: the first colour moves to the middle one over the first 0.55 of the leg and
 * on to the last over the rest.
 *
 * @param from The colour at the leg's start.
 * @param mid The colour at 0.55.
 * @param to The colour at its end.
 * @param p The leg's progress, 0 to 1.
 * @param out The ambient to write.
 */
function legInto(from: Rgb, mid: Rgb, to: Rgb, p: number, out: Ambient): void {
  if (p < TINT_SPLIT) {
    mixInto(from, mid, smooth(p / TINT_SPLIT), out);
  } else {
    mixInto(mid, to, smooth((p - TINT_SPLIT) / (1 - TINT_SPLIT)), out);
  }
}

/**
 * The sky's light at a minute of the turn on a calendar day: full light through the day, eased down through dusk to the
 * night's level, held through the dark and eased up through dawn, with the tint a colour curve across the turn.
 *
 * @param minute The minute of the turn, 0 up to its length.
 * @param day The game day, which sets the night's level through the moons.
 * @param sky The sky in force.
 * @param out The ambient to write.
 * @returns `out`.
 */
export function ambientAtMinute(minute: number, day: number, sky: SkyOverride, out: Ambient): Ambient {
  const table = SKIES[sky];
  const level = nightLevel(day, sky);
  const m = Number.isFinite(minute) ? ((minute % MINUTES_PER_TURN) + MINUTES_PER_TURN) % MINUTES_PER_TURN : 0;
  if (m < DAWN_END) {
    const p = m / DAWN_END;
    out.ambient = level + (1 - level) * smooth(p);
    legInto(table.night, table.dawn, table.gold, p, out);
  } else if (m < DUSK_START) {
    out.ambient = 1;
    holdInto(table.gold, out);
  } else if (m < DUSK_END) {
    const p = (m - DUSK_START) / PHASES.dusk;
    out.ambient = 1 - (1 - level) * smooth(p);
    legInto(table.gold, table.dusk, table.night, p, out);
  } else {
    out.ambient = level;
    holdInto(table.night, out);
  }
  out.tint = 1 - out.ambient + table.dayWash * out.ambient;
  return out;
}

/**
 * The sky's light at a wall-clock time: the minute of the turn and the calendar day come from the epoch alone, so every
 * page and server agrees without anything on the wire.
 *
 * @param epochMs The time in milliseconds since the Unix epoch, the server's when the page knows its offset.
 * @param sky The sky in force.
 * @param out The ambient to write.
 * @returns `out`.
 */
export function ambientAt(epochMs: number, sky: SkyOverride, out: Ambient): Ambient {
  return ambientAtMinute(turnMinuteOf(epochMs), gameDayOf(epochMs), sky, out);
}

/**
 * How much of the night's darkness a character under an open roof on an outdoor map is spared (the author's ruling of
 * 2026-11-01): 0 is the sky's own light, 1 full light. The darkness that remains is half of the sky's, and lamps, hearths
 * and candles light it as they light the open.
 */
export const ROOF_LIGHT_EASE = 0.5;

/**
 * How much of the night's darkness a separate indoor map is spared: three quarters of it, so a room is moody at night and
 * needs its candles and lamps without ever being as dark as the open (the author's ruling of 2026-11-01).
 */
export const INDOOR_LIGHT_EASE = 0.75;

/**
 * Eases a light toward full light: the darkness `1 - ambient` and the fill's tint shrink to `1 - ease` of themselves.
 *
 * @param sky The light, written in place.
 * @param ease The share of the darkness spared, held to 0 up to 1, and 0 when it is not a number.
 * @returns `sky`.
 */
export function easeLight(sky: Ambient, ease: number): Ambient {
  const kept = 1 - (Number.isFinite(ease) ? Math.min(1, Math.max(0, ease)) : 0);
  sky.ambient = 1 - (1 - sky.ambient) * kept;
  sky.tint *= kept;
  return sky;
}

/**
 * The darkest ambient a light eased by `easeLight` reaches, for the strength of the lights that glow to meet it.
 *
 * @param level The unspared darkest ambient (`nightLevel`).
 * @param ease The share of the darkness spared.
 * @returns The eased level.
 */
export function easedLevel(level: number, ease: number): number {
  return easeLight({ ambient: level, tint: 0, r: 0, g: 0, b: 0 }, ease).ambient;
}

/**
 * The light of an indoor map: the everyday sky's light, written in `out` by the caller (an indoor map follows no named sky),
 * with `INDOOR_LIGHT_EASE` of its darkness spared, under the room's fixed dim, the darker of the two holding (ADR-141
 * decision D4 as amended on 2026-11-01). By day it is held at one minus the dim, in the night's slate; at night it is
 * darker, but never as dark as the open.
 *
 * @param dim The map's dim; held to 0 up to `MAX_MAP_DIM`, and 0 when it is not a number.
 * @param out The everyday sky's light at the hour, written in place.
 * @returns `out`.
 */
export function indoorLight(dim: number, out: Ambient): Ambient {
  const held = Number.isFinite(dim) ? Math.min(MAX_MAP_DIM, Math.max(0, dim)) : 0;
  return withWeatherDim(easeLight(out, INDOOR_LIGHT_EASE), held, "none");
}

/**
 * Puts the weather under the sky's light (the author's rule of 2026-10-31: rain and night never stack). The final ambient is
 * the darker of the sky's and the weather's, `min(sky, 1 - dim)`, so rain at night adds nothing and rain by day dims to the
 * rain level. While the weather is the darker, the fill takes the sky's night colour, eased in with how far the weather is
 * below the sky so the colour does not jump where the two cross; the fill's alpha is `1 - ambient`, and a sky wash the day
 * carries (the blood moon's) stays under it.
 *
 * @param sky The sky's light, written in place; the sky name sets the night colour.
 * @param dim The weather's dim, 0 for none up to 1; held to that range, and 0 when it is not a number.
 * @param name The sky in force, which names the night colour.
 * @returns `sky`.
 */
export function withWeatherDim(sky: Ambient, dim: number, name: SkyOverride): Ambient {
  const held = Number.isFinite(dim) ? Math.min(1, Math.max(0, dim)) : 0;
  const weather = 1 - held;
  if (weather >= sky.ambient) {
    return sky;
  }
  const weight = (sky.ambient - weather) / (1 - weather);
  const night = SKIES[name].night;
  const wash = SKIES[name].dayWash;
  sky.r += (night[0] - sky.r) * weight;
  sky.g += (night[1] - sky.g) * weight;
  sky.b += (night[2] - sky.b) * weight;
  sky.ambient = weather;
  sky.tint = Math.max(sky.tint, 1 - weather + wash * weather);
  return sky;
}
